import {
  type PendingConnectorTarget,
  type RevitOperationProgressSnapshot,
} from '../../../api/grpc/operationProgressClient';
import { type ConnectorTargetOutcome } from '../../connectorTarget';
import { type ParameterBindingKind } from '../../revit/projectParameterMutationPlan';
import {
  type ConnectorParameterUndoPlan,
} from '../../../stores/useUIStore';
import type {
  ConnectorParameterUndoEntry,
  ConnectorProjectParameterUndoEntry,
} from '../../../types/parameterSetupUndo';
import { type OperationProgressModel } from '../../../ui/progress';
import { type ConnectorParameterTarget } from '../../../types/config/connector.types';

export type ParameterAdditionMethod = 'project' | 'family';

export interface AvailableProjectParameterFetchOptions {
  cacheScopeKey?: string | null;
  force?: boolean;
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}

export type ParameterAvailabilityCheckOptions =
  | string
  | null
  | AvailableProjectParameterFetchOptions;

export interface ConnectorAdditionOperation {
  scope: 'pipeSystem' | 'editableFamilies' | 'all';
  destination: 'project' | 'family';
  bindingKind: ParameterBindingKind;
  categories: string[];
  parameterNames: string[];
  parameterTypeHints?: Array<{ name: string; dataType: string }>;
}

export interface AdditionalCategoryAdditionOperation {
  destination: 'project' | 'family';
  bindingKind: ParameterBindingKind;
  categories?: string[];
  parameterNames: string[];
  targetObjectTypes?: string[];
  labels?: string[];
}

export interface ParameterAdditionResult {
  success: boolean;
  addedCount: number;
  updatedCount?: number;
  existingCount?: number;
  failedCount?: number;
  conflictingCount?: number;
  bindingConflictExistingCount?: number;
  familyCount?: number; // Number of families processed
  modifiedFamilyCount?: number;
  resolvedFamilyTargetCount?: number;
  processedFamilyTargetCount?: number;
  openedFamilyCount?: number;
  unmodifiedFamilyTargetCount?: number;
  hasSystemFamilies?: boolean; // Whether system families were processed
  hasLoadableFamilies?: boolean; // Whether loadable families were processed
  queued?: boolean;
  requestedCount?: number;
  undoAvailable?: boolean;
  projectUndoEntries?: ConnectorProjectParameterUndoEntry[];
  familyUndoEntries?: ConnectorParameterUndoEntry[];
  failedParameters?: string[];
  conflictingParameters?: string[];
  failureMessages?: string[];
  blockedByPendingOperation?: boolean;
  blockedByActiveOperation?: boolean;
  error?: string;
}

export interface ParameterAdditionOptions {
  suppressNotifications?: boolean;
  familyTargetMode?: 'allMatching' | 'newSincePreviousConnectorSetup';
  connectorTarget?: ConnectorParameterTarget;
}

export interface ConnectorTargetTransactionResult {
  outcome: ConnectorTargetOutcome;
  /** The rich mutation result, or nothing when no mutation was attempted. */
  applyResult: ParameterAdditionResult | null;
}

export interface ProcessProjectParameterScope {
  processConditionParameterNames: string[];
  projectPropertyParameterNames: string[];
}

export interface FamilyParameterHostResponseData {
  modifiedFamiliesCount?: number;
  resolvedFamilyTargetCount?: number;
  processedFamilyTargetCount?: number;
  openedFamilyCount?: number;
  unmodifiedFamilyTargetCount?: number;
  existingFamilyParameterCount?: number;
  conflictCount?: number;
  failureCount?: number;
  undoEntries?: ConnectorParameterUndoEntry[];
  projectUndoEntries?: ConnectorProjectParameterUndoEntry[];
  status?: string;
  message?: string;
  error?: string;
}

export interface ResumedFamilyParameterHostResult extends FamilyParameterHostResponseData {
  success: boolean;
  modifiedFamiliesCount?: number;
  systemFamilyParamsAdded?: number;
}

export function mapResumedFamilyParameterResult(
  result: ResumedFamilyParameterHostResult,
): ParameterAdditionResult {
  const failedCount = result.failureCount ?? 0;
  const conflictingCount = result.conflictCount ?? 0;
  const modifiedFamilyCount = result.modifiedFamiliesCount ?? 0;
  const systemFamilyParamsAdded = result.systemFamilyParamsAdded ?? 0;
  const success = result.success && failedCount === 0 && conflictingCount === 0;

  return {
    success,
    addedCount: systemFamilyParamsAdded,
    existingCount: result.existingFamilyParameterCount,
    familyCount: result.processedFamilyTargetCount ??
      result.resolvedFamilyTargetCount ??
      modifiedFamilyCount,
    modifiedFamilyCount,
    resolvedFamilyTargetCount: result.resolvedFamilyTargetCount,
    processedFamilyTargetCount: result.processedFamilyTargetCount,
    openedFamilyCount: result.openedFamilyCount,
    unmodifiedFamilyTargetCount: result.unmodifiedFamilyTargetCount,
    hasLoadableFamilies: true,
    hasSystemFamilies: systemFamilyParamsAdded > 0,
    queued: false,
    failedCount,
    conflictingCount,
    familyUndoEntries: result.undoEntries,
    projectUndoEntries: result.projectUndoEntries,
    error: success ? undefined : result.error || result.message ||
      'Family parameter setup did not complete.',
  };
}

export interface ParameterAvailabilitySummary {
  requestedCount: number;
  availableBeforeCount: number;
  availableAfterCount: number;
  newlyAvailableCount: number;
  missingCount: number;
}

export interface HostConflictFailureCounts {
  conflictingCount: number;
  failedCount: number;
}

export interface HostFamilyParameterFeedback extends HostConflictFailureCounts {
  detail?: string;
}

export interface ParameterCompletionNotification {
  severity: 'success' | 'warning' | 'info';
  message: string;
}

/**
 * Typed connector operation state for the UI: the immutable owner/target of
 * the invocation or recovered record currently holding the connector step,
 * plus an English description built from that frozen context — never from
 * live configuration values.
 */
export interface ActiveConnectorOperationUiState {
  owner: string;
  target: PendingConnectorTarget | null;
  description: string;
}

export interface UseParameterAdditionReturn {
  isAdding: boolean;
  parameterProgress: OperationProgressModel | null;
  activeConnectorOperation: ActiveConnectorOperationUiState | null;
  connectorSelectionBlockedByMismatch: boolean;
  addParameters: (
    options?: ParameterAdditionOptions,
  ) => Promise<ParameterAdditionResult>;
  /**
   * Replaces the connector target as one transaction: any previously applied
   * target is undone first, and the setup revision is told what happened.
   */
  applyConnectorTarget: (
    options?: ParameterAdditionOptions,
  ) => Promise<ConnectorTargetTransactionResult>;
  undoLastConnectorParameterAddition: () => Promise<boolean>;
  lastConnectorUndoPlan: ConnectorParameterUndoPlan | null;
  addAdditionalCategoryParameters: () => Promise<ParameterAdditionResult>;
  undoLastAdditionalCategoryParameterAddition: () => Promise<boolean>;
  lastAdditionalCategoryUndoPlan: ConnectorParameterUndoPlan | null;
  addProcessAndProjectParameters: (
    options?: ParameterAdditionOptions,
    scope?: ProcessProjectParameterScope,
  ) => Promise<ParameterAdditionResult>;
  addMappedProjectParameters: (
    parameterNames: string[],
    options?: ParameterAdditionOptions,
  ) => Promise<ParameterAdditionResult>;
  getConfiguredParameters: () => string[];
}
